// ---------------------------------
// client/src/pages/Dashboard.jsx
// Workspace view with searchable & grouped job lists
// ---------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jwt_decode from 'jwt-decode';

import { createJob, fetchCurrentJobs, fetchJobTypes } from '../utils/jobs';
import { computeJobProgress, JOB_STATUS_LABELS, ACTIVE_JOB_STATUS_ORDER, JOB_STATUS_ARCHIVED } from '../utils/jobProgress';
import { formatDate } from '../utils/date';

const TEXT = {
    pageTitle: 'לוח עבודות',
    filtersTitle: 'סינון וחיפוש',
    searchLabel: 'חיפוש עבודה / לקוח',
    searchPlaceholder: 'לדוגמה: 2045, J-2045 או שם לקוח',
    jobTypeFilter: 'סוגי עבודה',
    statusFilter: 'סטטוסים',
    showArchived: 'הצג עבודות בארכיון',
    clearFilters: 'נקה מסננים',
    noJobsMatching: 'לא נמצאו עבודות בהתאם לסינון.',
    sectionEmpty: 'אין עבודות בקטגוריה זו.',
    sectionToggle: 'לחץ לפתיחה או סגירה של הרשימה',
    createJobAction: 'צור עבודה חדשה',
    modalTitle: 'פתיחת עבודה חדשה',
    jobNumber: 'מספר עבודה',
    jobNumberHint: 'מספר עבודה (ספרות בלבד)',
    jobNumberHelper: 'מספר העבודה יישמר בפורמט J-####.',
    jobType: 'בחר סוגי עבודה',
    notes: 'הערות (אופציונלי)',
    recipientCompanyName: 'שם החברה המקבלת שירות',
    privateJob: 'עבודה פרטית',
    privateJobHint: 'יהיה ניתן לכנס לעבודה רק דרך המשתמש הנוכחי.',
    recipientCompanyHint: 'למשל: שם הלקוח או החברה',
    cancel: 'ביטול',
    save: 'שמירה',
    loading: 'טוען עבודות...',
    saving: 'שומר...',
    loadError: 'אירעה שגיאה בטעינת העבודות.',
    validationError: 'יש להזין מספר עבודה ולבחור לפחות סוג עבודה אחד.',
    createError: 'לא ניתן ליצור עבודה חדשה.',
    noJobTypes: 'לא הוגדרו סוגי עבודה פעילים. פנה למנהל.',
    lastUpdated: 'עודכן לאחרונה',
    formsCompleted: 'טפסים הושלמו',
    formsPending: 'טפסים ממתינים',
    viewJob: 'פתח עבודה',
    previewTitle: 'טפסים מוצעים',
    previewHint: 'בחר סוג עבודה כדי לראות אילו טפסים יתווספו אוטומטית.',
    allJobTypes: 'כל סוגי העבודה',
    noJobTypesSelected: 'לא נבחרו סוגי עבודה',
};

const STATUS_ICON_PROPS = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
};

const STATUS_ICON_MAP = {
    in_progress: (
        <svg {...STATUS_ICON_PROPS}>
            <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M11 9.5L16 12l-5 2.5V9.5Z" fill="currentColor" />
        </svg>
    ),
    not_started: (
        <svg {...STATUS_ICON_PROPS}>
            <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
            <path
                d="M12 7v5l3 1.5"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    ),
    completed: (
        <svg {...STATUS_ICON_PROPS}>
            <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
            <path
                d="M9.5 12.5l2.1 2 3.9-4.6"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    ),
    archived: (
        <svg {...STATUS_ICON_PROPS}>
            <rect x="7" y="7" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" fill="none" />
            <path d="M9 9.5h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M9 12h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    default: (
        <svg {...STATUS_ICON_PROPS}>
            <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
        </svg>
    ),
};

const CHEVRON_ICON_MAP = {
    expanded: (
        <svg {...STATUS_ICON_PROPS}>
            <path
                d="M6.5 14.5 12 9l5.5 5.5"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    ),
    collapsed: (
        <svg {...STATUS_ICON_PROPS}>
            <path
                d="M6.5 9.5 12 15l5.5-5.5"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    ),
};

function StatusIcon({ status }) {
    return (
        <span className="dashboard-section__icon" aria-hidden="true">
            {STATUS_ICON_MAP[status] || STATUS_ICON_MAP.default}
        </span>
    );
}

function ChevronIcon({ expanded }) {
    return (
        <span className="dashboard-section__chevron" aria-hidden="true">
            {expanded ? CHEVRON_ICON_MAP.expanded : CHEVRON_ICON_MAP.collapsed}
        </span>
    );
}

function buildJobTypeSummary(options, selectedIds, emptyLabel) {
    const selectedNames = options
        .filter((jobType) => selectedIds.has(String(jobType.id)))
        .map((jobType) => jobType.name)
        .filter(Boolean);

    if (selectedNames.length === 0) {
        return emptyLabel;
    }
    if (selectedNames.length <= 2) {
        return selectedNames.join(', ');
    }
    return `נבחרו ${selectedNames.length} סוגי עבודה`;
}

function MultiSelectDropdown({
    label,
    summary,
    options,
    selectedIds,
    onToggle,
    emptyText,
}) {
    return (
        <div className="stack">
            <span className="form-field__label">{label}</span>
            <details className="multi-select-dropdown">
                <summary className="multi-select-dropdown__summary">
                    <span className="multi-select-dropdown__summary-text">{summary}</span>
                </summary>
                <div className="multi-select-dropdown__menu">
                    {options.length === 0 ? (
                        <span className="card__meta">{emptyText}</span>
                    ) : (
                        options.map((jobType) => {
                            const id = String(jobType.id);
                            const isSelected = selectedIds.has(id);
                            return (
                                <label
                                    key={jobType.id}
                                    className={`multi-select-dropdown__option${isSelected ? ' multi-select-dropdown__option--selected' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => onToggle(id)}
                                    />
                                    <span>{jobType.name}</span>
                                </label>
                            );
                        })
                    )}
                </div>
            </details>
        </div>
    );
}

function normalizeJobNumberInput(rawValue) {
    if (rawValue === null || rawValue === undefined) {
        return '';
    }
    return String(rawValue).replace(/\D+/g, '');
}

const STATUS_CLASS = {
    in_progress: 'status-badge--info',
    not_started: 'status-badge--warning',
    completed: 'status-badge--success',
    archived: 'status-badge--danger',
};

export default function Dashboard() {
    const navigate = useNavigate();

    const [jobs, setJobs] = useState([]);
    const [jobTypes, setJobTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedJobTypes, setSelectedJobTypes] = useState(() => new Set());
    const [statusFilters, setStatusFilters] = useState(() => new Set(ACTIVE_JOB_STATUS_ORDER));
    const [showArchived, setShowArchived] = useState(false);
    const [collapsedSections, setCollapsedSections] = useState({
        in_progress: false,
        not_started: true,
        completed: true,
        [JOB_STATUS_ARCHIVED]: true,
    });
    const [hasInitializedSections, setHasInitializedSections] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newJobData, setNewJobData] = useState({
        jobNumber: '',
        jobTypeIds: [],
        notes: '',
        recipientCompanyName: '',
        isPrivate: false,
    });
    const [formError, setFormError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isAdminUser = useMemo(() => {
        const token = localStorage.getItem('token');
        if (!token) return false;
        try {
            return jwt_decode(token)?.role === 'admin';
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                setIsLoading(true);
                setError('');
                const [jobsResponse, jobTypesResponse] = await Promise.all([
                    fetchCurrentJobs(),
                    fetchJobTypes(),
                ]);
                if (!isMounted) return;
                setJobs(jobsResponse);
                setJobTypes(jobTypesResponse);
            } catch (err) {
                console.error('Error loading dashboard data', err);
                if (isMounted) {
                    setError(err.response?.data?.message || TEXT.loadError);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        load();
        return () => {
            isMounted = false;
        };
    }, []);

    const jobTypeOptions = useMemo(() => {
        return [...jobTypes].sort((a, b) => a.name.localeCompare(b.name, 'he'));
    }, [jobTypes]);

    const suggestedTemplatesPreview = useMemo(() => {
        const selectedIds = new Set((newJobData.jobTypeIds || []).map((id) => String(id)));
        if (selectedIds.size === 0) {
            return [];
        }
        const templateMap = new Map();
        jobTypeOptions.forEach((jobType) => {
            const jobTypeId = String(jobType.id);
            if (!selectedIds.has(jobTypeId)) {
                return;
            }
            (jobType.suggestedTemplates || []).forEach((template) => {
                const templateId = String(template.id);
                const existing = templateMap.get(templateId);
                const isRequired = Boolean(template.isRequired);
                if (existing) {
                    if (isRequired) existing.isRequired = true;
                    if (!existing.sources.includes(jobType.name)) {
                        existing.sources.push(jobType.name);
                    }
                } else {
                    templateMap.set(templateId, {
                        id: templateId,
                        name: template.name,
                        isRequired,
                        sources: jobType.name ? [jobType.name] : [],
                    });
                }
            });
        });
        return Array.from(templateMap.values()).sort((a, b) => a.name.localeCompare(b.name, 'he'));
    }, [jobTypeOptions, newJobData.jobTypeIds]);
    const selectedCreateJobTypes = useMemo(
        () => new Set((newJobData.jobTypeIds || []).map((id) => String(id))),
        [newJobData.jobTypeIds],
    );
    const jobTypeFilterSummary = useMemo(
        () => buildJobTypeSummary(jobTypeOptions, selectedJobTypes, TEXT.allJobTypes),
        [jobTypeOptions, selectedJobTypes],
    );
    const createJobTypeSummary = useMemo(
        () => buildJobTypeSummary(jobTypeOptions, selectedCreateJobTypes, TEXT.noJobTypesSelected),
        [jobTypeOptions, selectedCreateJobTypes],
    );

    const jobProgressRows = useMemo(() => jobs.map((job) => computeJobProgress(job)), [jobs]);

    const statusCounts = useMemo(() => {
        return jobProgressRows.reduce((acc, item) => {
            acc[item.status] = (acc[item.status] || 0) + 1;
            return acc;
        }, {});
    }, [jobProgressRows]);

    useEffect(() => {
        if (hasInitializedSections || isLoading) {
            return;
        }
        setCollapsedSections({
            in_progress: false,
            not_started: true,
            completed: true,
            [JOB_STATUS_ARCHIVED]: true,
        });
        setHasInitializedSections(true);
    }, [hasInitializedSections, isLoading, statusCounts]);

    const filteredJobs = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const normalizedTerm = normalizeJobNumberInput(searchTerm).toLowerCase();
        const hasJobTypeFilter = selectedJobTypes.size > 0;
        const hasStatusFilter = statusFilters.size > 0 && statusFilters.size < ACTIVE_JOB_STATUS_ORDER.length;
        return jobProgressRows.filter((item) => {
            const isArchivedJob = item.status === JOB_STATUS_ARCHIVED;

            if (!showArchived && isArchivedJob) {
                return false;
            }

            if (term) {
                const jobNumberValue = (item.jobNumber || '').toLowerCase();
                const normalizedJobNumber = normalizeJobNumberInput(item.jobNumber || '').toLowerCase();
                const customerName = (item.raw?.recipientCompanyName || '').toLowerCase();
                const matchesDisplay = jobNumberValue.includes(term);
                const matchesNormalized = normalizedTerm ? normalizedJobNumber.includes(normalizedTerm) : false;
                const matchesCustomer = customerName.includes(term);
                if (!matchesDisplay && !matchesNormalized && !matchesCustomer) {
                    return false;
                }
            }
            if (hasJobTypeFilter) {
                const jobTypeIds =
                    Array.isArray(item.jobTypeIds) && item.jobTypeIds.length > 0
                        ? item.jobTypeIds
                        : [
                              ...(item.raw?.jobType?.id ? [String(item.raw.jobType.id)] : []),
                              ...(item.raw?.jobTypeId ? [String(item.raw.jobTypeId)] : []),
                          ];
                const matchesJobType = jobTypeIds.some((id) => selectedJobTypes.has(String(id)));
                if (!matchesJobType) {
                    return false;
                }
            }
            if (!isArchivedJob && hasStatusFilter && !statusFilters.has(item.status)) {
                return false;
            }
            return true;
        });
    }, [jobProgressRows, searchTerm, selectedJobTypes, statusFilters, showArchived]);

    const groupedJobs = useMemo(() => {
        const groups = {
            [JOB_STATUS_ARCHIVED]: [],
        };
        ACTIVE_JOB_STATUS_ORDER.forEach((status) => {
            groups[status] = [];
        });
        filteredJobs.forEach((item) => {
            if (!groups[item.status]) {
                groups[item.status] = [];
            }
            groups[item.status].push(item);
        });
        ACTIVE_JOB_STATUS_ORDER.forEach((statusKey) => {
            groups[statusKey].sort((a, b) => b.lastActivity - a.lastActivity);
        });
        groups[JOB_STATUS_ARCHIVED].sort((a, b) => b.lastActivity - a.lastActivity);
        return groups;
    }, [filteredJobs]);

    const toggleJobTypeFilter = (jobTypeId) => {
        setSelectedJobTypes((prev) => {
            const next = new Set(prev);
            if (next.has(jobTypeId)) {
                next.delete(jobTypeId);
            } else {
                next.add(jobTypeId);
            }
            return next;
        });
    };

    const toggleStatusFilter = (statusKey) => {
        setStatusFilters((prev) => {
            const next = new Set(prev);
            if (next.has(statusKey)) {
                next.delete(statusKey);
            } else {
                next.add(statusKey);
            }
            if (next.size === 0) {
                return new Set(ACTIVE_JOB_STATUS_ORDER);
            }
            return next;
        });
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedJobTypes(new Set());
        setStatusFilters(new Set(ACTIVE_JOB_STATUS_ORDER));
        setShowArchived(false);
    };

    const toggleSection = (statusKey) => {
        setCollapsedSections((prev) => ({
            ...prev,
            [statusKey]: !prev[statusKey],
        }));
    };

    const openCreateModal = () => {
        setNewJobData({
            jobNumber: '',
            jobTypeIds: jobTypeOptions.length === 1 ? [String(jobTypeOptions[0].id)] : [],
            notes: '',
            recipientCompanyName: '',
            isPrivate: false,
        });
        setFormError('');
        setIsModalOpen(true);
    };

    const closeCreateModal = () => {
        if (isSubmitting) {
            return;
        }
        setIsModalOpen(false);
    };

    const handleToggleModalJobType = (jobTypeId) => {
        setNewJobData((prev) => {
            const id = String(jobTypeId);
            const current = new Set(prev.jobTypeIds || []);
            if (current.has(id)) {
                current.delete(id);
            } else {
                current.add(id);
            }
            return {
                ...prev,
                jobTypeIds: Array.from(current),
            };
        });
    };

    const handleCreateJob = async (event) => {
        event.preventDefault();
        setFormError('');
        const jobNumberInput = newJobData.jobNumber.trim();
        const normalizedJobNumber = normalizeJobNumberInput(jobNumberInput);
        const jobTypeIds = Array.isArray(newJobData.jobTypeIds)
            ? Array.from(new Set(newJobData.jobTypeIds.filter(Boolean).map((value) => String(value))))
            : [];
        if (!normalizedJobNumber || jobTypeIds.length === 0) {
            setFormError(TEXT.validationError);
            return;
        }
        try {
            setIsSubmitting(true);
            const created = await createJob({
                jobNumber: normalizedJobNumber,
                jobTypeIds,
                notes: newJobData.notes.trim() || undefined,
                recipientCompanyName: newJobData.recipientCompanyName.trim() || undefined,
                isPrivate: isAdminUser ? Boolean(newJobData.isPrivate) : false,
            });
            if (created?.alreadyExists && created?.id) {
                setIsModalOpen(false);
                navigate(`/jobs/${created.id}`);
                return;
            }
            if (created?.id) {
                setIsModalOpen(false);
                setNewJobData({ jobNumber: '', jobTypeIds: [], notes: '', recipientCompanyName: '', isPrivate: false });
                navigate(`/jobs/${created.id}`);
                return;
            }
            setJobs((prev) => [created, ...prev]);
            setIsModalOpen(false);
            setNewJobData({ jobNumber: '', jobTypeIds: [], notes: '', recipientCompanyName: '', isPrivate: false });
        } catch (err) {
            console.error('Failed creating job', err);
            setFormError(err.response?.data?.message || TEXT.createError);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderJobCard = (job) => {
        const remaining =
            job.status === 'completed' || job.status === JOB_STATUS_ARCHIVED
                ? 0
                : Math.max(job.counts.total - job.counts.completed, 0);
        const lastUpdated =
            job.lastActivity > 0 ? formatDate(job.lastActivity) : 'לא עודכן';
        return (
            <article
                key={job.jobId ?? job.jobNumber}
                className={[
                    'card',
                    'card--interactive',
                    'job-card',
                    `job-card--${job.status}`,
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                <header className="stack">
                    <div className="flex justify-between align-start flex--wrap">
                        <div className="stack">
                            <h3 className="card__title">{job.jobNumber || 'ללא מספר'}</h3>
                            <span className="card__meta">{job.jobTypeName || '-'}</span>
                        </div>
                        <span className={`status-badge ${STATUS_CLASS[job.status] || 'status-badge--info'}`}>
                            {JOB_STATUS_LABELS[job.status]}
                        </span>
                    </div>
                    <span className="card__meta">
                        {TEXT.lastUpdated}: {lastUpdated}
                    </span>
                </header>
                <div className="job-card__progress">
                    <div className="progress-bar" role="progressbar" aria-valuenow={job.progressPercent} aria-valuemin={0} aria-valuemax={100}>
                        <div className="progress-bar__fill" style={{ width: `${job.progressPercent}%` }} />
                    </div>
                    <div className="job-card__metrics">
                        <span>
                            {TEXT.formsCompleted}: {job.counts.completed}/{job.counts.total}
                        </span>
                        <span>
                            {TEXT.formsPending}: {remaining}
                        </span>
                    </div>
                </div>
                <footer className="flex flex--wrap">
                    <button
                        type="button"
                        className="button button--primary"
                        onClick={() => navigate(`/jobs/${job.jobId}`)}
                    >
                        {TEXT.viewJob}
                    </button>
                </footer>
            </article>
        );
    };

    return (
        <div className="page page--dashboard page--accent stack stack--lg">
            <header className="page__header">
                <div className="stack">
                    <h1 className="page__title">{TEXT.pageTitle}</h1>
                    <p className="page__description">
                        {TEXT.formsPending}: {jobs.reduce((acc, job) => acc + (job.pendingForms ?? 0), 0)}
                    </p>
                </div>
                <button type="button" className="button button--primary" onClick={openCreateModal}>
                    {TEXT.createJobAction}
                </button>
            </header>

            {error && <div className="alert alert--danger">{error}</div>}

            <section className="card dashboard-filters">
                <header className="dashboard-filters__header">
                    <h2 className="card__title">{TEXT.filtersTitle}</h2>
                    <button type="button" className="link-button" onClick={clearFilters}>
                        {TEXT.clearFilters}
                    </button>
                </header>
                <div className="dashboard-filters__grid">
                    <label className="form-field">
                        <span className="form-field__label">{TEXT.searchLabel}</span>
                        <input
                            className="form-field__control"
                            type="search"
                            value={searchTerm}
                            placeholder={TEXT.searchPlaceholder}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                    </label>
                    <MultiSelectDropdown
                        label={TEXT.jobTypeFilter}
                        summary={jobTypeFilterSummary}
                        options={jobTypeOptions}
                        selectedIds={selectedJobTypes}
                        onToggle={toggleJobTypeFilter}
                        emptyText={TEXT.noJobTypes}
                    />
                    <div className="stack">
                        <span className="form-field__label">{TEXT.statusFilter}</span>
                        <div className="filter-chips">
                            {ACTIVE_JOB_STATUS_ORDER.map((statusKey) => {
                                const isActive = statusFilters.has(statusKey);
                                return (
                                    <label key={statusKey} className={`chip chip--filter${isActive ? ' chip--filter--active' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={isActive}
                                            onChange={() => toggleStatusFilter(statusKey)}
                                        />
                                        <span>
                                            {JOB_STATUS_LABELS[statusKey]} ({statusCounts[statusKey] || 0})
                                        </span>
                                    </label>
                                );
                            })}
                            {isAdminUser && (
                                <label className={`chip chip--filter${showArchived ? ' chip--filter--active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={showArchived}
                                        onChange={(event) => setShowArchived(event.target.checked)}
                                    />
                                    <span>{TEXT.showArchived}</span>
                                </label>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {isLoading ? (
                <div className="card">{TEXT.loading}</div>
            ) : (
                <>
                    {filteredJobs.length === 0 && (
                        <div className="card">{TEXT.noJobsMatching}</div>
                    )}
                    {ACTIVE_JOB_STATUS_ORDER.map((statusKey) => {
                        const jobsForStatus = groupedJobs[statusKey];
                        const isCollapsed = collapsedSections[statusKey];
                        return (
                            <section key={statusKey} className="dashboard-section">
                                <button
                                    type="button"
                                    className={`dashboard-section__header dashboard-section__header--${statusKey}`}
                                    onClick={() => toggleSection(statusKey)}
                                    aria-expanded={!isCollapsed}
                                >
                                    <StatusIcon status={statusKey} />
                                    <span>{JOB_STATUS_LABELS[statusKey]}</span>
                                    <span className="dashboard-section__count">{jobsForStatus.length}</span>
                                    <ChevronIcon expanded={!isCollapsed} />
                                    <span className="sr-only">{TEXT.sectionToggle}</span>
                                </button>
                                {!isCollapsed && (
                                    jobsForStatus.length === 0 ? (
                                        <div className="card">{TEXT.sectionEmpty}</div>
                                    ) : (
                                        <div className="card-grid card-grid--two">
                                            {jobsForStatus.map((job) => renderJobCard(job))}
                                        </div>
                                    )
                                )}
                            </section>
                        );
                    })}
                    {isAdminUser && showArchived && (
                        <section key={JOB_STATUS_ARCHIVED} className="dashboard-section">
                            <button
                                type="button"
                                className={`dashboard-section__header dashboard-section__header--${JOB_STATUS_ARCHIVED}`}
                                onClick={() => toggleSection(JOB_STATUS_ARCHIVED)}
                                aria-expanded={!collapsedSections[JOB_STATUS_ARCHIVED]}
                            >
                                <StatusIcon status={JOB_STATUS_ARCHIVED} />
                                <span>{JOB_STATUS_LABELS[JOB_STATUS_ARCHIVED]}</span>
                                <span className="dashboard-section__count">
                                    {groupedJobs[JOB_STATUS_ARCHIVED]?.length || 0}
                                </span>
                                <ChevronIcon expanded={!collapsedSections[JOB_STATUS_ARCHIVED]} />
                                <span className="sr-only">{TEXT.sectionToggle}</span>
                            </button>
                            {!collapsedSections[JOB_STATUS_ARCHIVED] && (
                                (groupedJobs[JOB_STATUS_ARCHIVED] || []).length === 0 ? (
                                    <div className="card">{TEXT.sectionEmpty}</div>
                                ) : (
                                    <div className="card-grid card-grid--two">
                                        {groupedJobs[JOB_STATUS_ARCHIVED].map((job) => renderJobCard(job))}
                                    </div>
                                )
                            )}
                        </section>
                    )}
                </>
            )}

            {isModalOpen && (
                <div className="modal-overlay" role="dialog" aria-modal="true">
                    <div className="modal-content">
                        <form className="modal-form" onSubmit={handleCreateJob}>
                            <header className="stack">
                                <h2 className="card__title">{TEXT.modalTitle}</h2>
                            </header>
                            <div className="modal-form__body">
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.jobNumber}</span>
                                    <input
                                        className="form-field__control"
                                        type="text"
                                        value={newJobData.jobNumber}
                                        placeholder={TEXT.jobNumberHint}
                                        onChange={(event) =>
                                            setNewJobData((prev) => ({
                                                ...prev,
                                                jobNumber: normalizeJobNumberInput(event.target.value),
                                            }))
                                        }
                                    />
                                    <span className="form-field__description">{TEXT.jobNumberHelper}</span>
                                </label>
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.recipientCompanyName}</span>
                                    <input
                                        className="form-field__control"
                                        type="text"
                                        value={newJobData.recipientCompanyName}
                                        placeholder={TEXT.recipientCompanyHint}
                                        onChange={(event) =>
                                            setNewJobData((prev) => ({
                                                ...prev,
                                                recipientCompanyName: event.target.value,
                                            }))
                                        }
                                    />
                                </label>
                                {isAdminUser && (
                                    <div className="stack">
                                        <label className="check-toggle">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(newJobData.isPrivate)}
                                                onChange={(event) =>
                                                    setNewJobData((prev) => ({
                                                        ...prev,
                                                        isPrivate: event.target.checked,
                                                    }))
                                                }
                                            />
                                            <span>{TEXT.privateJob}</span>
                                        </label>
                                        <span className="form-field__description">{TEXT.privateJobHint}</span>
                                    </div>
                                )}
                                <div className="modal-form__section">
                                    <MultiSelectDropdown
                                        label={TEXT.jobType}
                                        summary={createJobTypeSummary}
                                        options={jobTypeOptions}
                                        selectedIds={selectedCreateJobTypes}
                                        onToggle={handleToggleModalJobType}
                                        emptyText={TEXT.noJobTypes}
                                    />
                                </div>
                                <div className="stack modal-form__section">
                                    <span className="form-field__label">{TEXT.previewTitle}</span>
                                    {suggestedTemplatesPreview.length === 0 ? (
                                        <p className="card__meta modal-form__empty">
                                            {jobTypeOptions.length === 0
                                                ? TEXT.noJobTypes
                                                : TEXT.previewHint}
                                        </p>
                                    ) : (
                                        <div className="modal-preview">
                                            {suggestedTemplatesPreview.map((template) => (
                                                <div key={template.id} className="modal-preview__item">
                                                    <div className="modal-preview__info">
                                                        <strong>{template.name}</strong>
                                                        {template.sources.length > 0 && (
                                                            <span className="card__meta">
                                                                {template.sources.join(', ')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="modal-preview__badges">
                                                        {template.isRequired && <span className="chip chip--danger">חובה</span>}
                                                    </div>
                                                </div>
                                            ))}

                                        </div>
                                    )}
                                </div>
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.notes}</span>
                                    <textarea
                                        className="form-field__control form-field__control--textarea"
                                        rows={3}
                                        value={newJobData.notes}
                                        onChange={(event) =>
                                            setNewJobData((prev) => ({
                                                ...prev,
                                                notes: event.target.value,
                                            }))
                                        }
                                    />
                                </label>
                            </div>
                            {formError && <div className="alert alert--danger">{formError}</div>}
                            <footer className="modal-form__footer">
                                <button type="button" className="button button--ghost" onClick={closeCreateModal}>
                                    {TEXT.cancel}
                                </button>
                                <button type="submit" className="button button--primary" disabled={isSubmitting}>
                                    {isSubmitting ? TEXT.saving : TEXT.save}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
